'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { supabase } from '@/lib/supabaseClient';
import Breadcrumb from '@/components/Breadcrumb';

// ─── PHASE DATA ────────────────────────────────────────────────────────────────

interface RolloutPhase {
  slug: string;
  num: number;
  arabic: string;
  arabicMeaning: string;
  name: string;
  subtitle: string;
  status: 'ACTIVE NOW' | 'BUILDING' | 'UPCOMING' | 'VISION';
  statusColor: string;
  accentColor: string;
  bgColor: string;
  heroTagline: string;
  quranicAyat: string;
  quranicRef: string;
  quranicTheme: string;
  quranicThemeArabic: string;
  quranicHref?: string;
  description: string;
  goals: string[];
  messagingPillars: { title: string; body: string }[];
  targetSegments: { label: string; icon: string }[];
  features: string[];
  ctaLabel: string;
  ctaHref: string;
}

const ROLLOUT_PHASES: RolloutPhase[] = [
  {
    slug: 'names-reference-alpha',
    num: 1,
    arabic: 'الحواس',
    arabicMeaning: 'Sensing',
    name: 'Names Reference Alpha',
    subtitle: 'HAWAS · Phase One',
    status: 'ACTIVE NOW',
    statusColor: '#2d7a47',
    accentColor: '#2d8a87',
    bgColor: '#0a1a12',
    heroTagline: 'The foundation is laid. The Names are live.',
    quranicAyat: '"Soon We will show them Our Ayat in the horizons and in themselves, until it becomes clear to them that it is the Truth."',
    quranicRef: 'Quran 41:53',
    quranicTheme: 'Recognition through Signs',
    quranicThemeArabic: 'الآيات في الآفاق وفي الأنفس',
    quranicHref: 'https://quran.com/41/53',
    description: 'The Names Reference Alpha is the first public phase of the Haal Project. 90+ Names of Allah — searchable, cross-linked, and rooted in ecological and social observation. This is the sensing phase: we open the platform, invite early caretakers, and begin the work of recognition.',
    goals: [
      'Launch Names Reference with 90+ Names of Allah fully documented',
      'Establish the IRL Lens as a tool for ecological recognition',
      'Onboard the first cohort of early caretaker community members',
      'Validate the Sacred Language vocabulary with real users',
      'Build the foundation for phase-based community growth',
    ],
    messagingPillars: [
      {
        title: 'Recognition (Marifah)',
        body: 'The Names of Allah are not abstract theology — they are observable in creation. Every ecosystem, every community dynamic, every moment of provision reflects a Name. The platform makes this visible.',
      },
      {
        title: 'Caretakership (Khilafah)',
        body: 'We were sent as caretakers. The Haal Project is a tool for those who take that role seriously — engineers, teachers, gardeners, community organizers, the formerly incarcerated, students.',
      },
      {
        title: 'Sacred Language as Framework',
        body: 'Arabic terms like Haal, Mizan, Amanah, and Kulli are not decorative — they are precise. The platform uses Sacred Language as a technical vocabulary for understanding reality.',
      },
    ],
    targetSegments: [
      { label: 'Muslim Educators', icon: '📚' },
      { label: 'Community Organizers', icon: '🤝' },
      { label: 'Ecology & Environment Workers', icon: '🌿' },
      { label: 'Students & Researchers', icon: '🔬' },
      { label: 'Masjid & Islamic Center Staff', icon: '🕌' },
      { label: 'Formerly Incarcerated Muslims', icon: '🌱' },
    ],
    features: ['Names Reference (90+ Names)', 'IRL Lens — 9 cities', 'Keys to Kulli Haal', 'Open Letter: Deep Green', 'Community Signup'],
    ctaLabel: 'Explore Names Reference →',
    ctaHref: '/names-reference',
  },
  {
    slug: 'mrg-beta',
    num: 2,
    arabic: 'العلم',
    arabicMeaning: 'Knowing',
    name: 'Muslim Resource Guide Beta',
    subtitle: 'ILM · Phase Two',
    status: 'BUILDING',
    statusColor: '#c9a84c',
    accentColor: '#c9a84c',
    bgColor: '#0d1a0a',
    heroTagline: 'Knowledge made actionable. Resources made findable.',
    quranicAyat: '"Allah will raise those who have believed among you and those who were given knowledge, by degrees."',
    quranicRef: 'Quran 58:11',
    quranicTheme: 'Elevation through Knowledge',
    quranicThemeArabic: 'يَرْفَعِ اللَّهُ الَّذِينَ آمَنُوا',
    quranicHref: 'https://quran.com/58/11',
    description: 'The Muslim Resource Guide Beta expands the platform into actionable community knowledge. City-specific resources, justice-involved support networks, ecological observation submissions, and caretaker quest generation. This is the knowing phase: we deepen the platform with real community data.',
    goals: [
      'Expand MRG to 15+ cities with verified local resources',
      'Launch community observation submission system (ObsPanel)',
      'Deploy first caretaker quest generation engine',
      'Build justice-involved Muslim support network directory',
      'Establish ambassador referral tracking for community growth',
    ],
    messagingPillars: [
      {
        title: 'Ilm as Infrastructure',
        body: 'Knowledge is not passive. The MRG turns Islamic knowledge into navigable infrastructure — connecting people to resources, organizations, and each other across cities.',
      },
      {
        title: 'Observation as Worship',
        body: 'The ObsPanel allows caretakers to submit ecological and social observations, building a living dataset of how the Names of Allah manifest in their local environments.',
      },
      {
        title: 'Justice as Caretakership',
        body: 'The formerly incarcerated are among the most important caretakers. The MRG Beta builds specific pathways for reentry, legal support, and community reintegration rooted in Islamic framework.',
      },
    ],
    targetSegments: [
      { label: 'Reentry & Justice-Involved', icon: '⚖️' },
      { label: 'Social Workers', icon: '🏥' },
      { label: 'Imams & Islamic Scholars', icon: '📖' },
      { label: 'Urban Planners & Designers', icon: '🏙️' },
      { label: 'Nonprofit & NGO Workers', icon: '🌐' },
      { label: 'Muslim Healthcare Workers', icon: '🩺' },
    ],
    features: ['MRG — 15+ cities', 'ObsPanel submissions', 'Caretaker quest engine', 'Justice-involved directory', 'Ambassador referral tracking'],
    ctaLabel: 'Explore Muslim Resource Guide →',
    ctaHref: '/muslim-resource-guide',
  },
  {
    slug: 'irl-lens-pilot',
    num: 3,
    arabic: 'الفكر',
    arabicMeaning: 'Reflecting',
    name: 'IRL Lens Pilot',
    subtitle: 'FIKR · Phase Three',
    status: 'UPCOMING',
    statusColor: '#8a8ab8',
    accentColor: '#8a8ab8',
    bgColor: '#0a0d1a',
    heroTagline: 'From screen to street. The lens goes physical.',
    quranicAyat: '"Do they not reflect upon themselves? Allah did not create the heavens and the earth and what is between them except in truth and for a specified term."',
    quranicRef: 'Quran 30:8',
    quranicTheme: 'Reflection as Method',
    quranicThemeArabic: 'أَوَلَمْ يَتَفَكَّرُوا فِي أَنفُسِهِمْ',
    quranicHref: 'https://quran.com/30/8',
    description: 'The IRL Lens Pilot takes the platform into physical space. Community circles, in-person caretaker kits, regional workshops, and educator curriculum partnerships. This is the reflecting phase: we move from digital recognition to embodied practice.',
    goals: [
      'Launch first 3 in-person IRL Lens community circles',
      'Distribute physical caretaker kits to 10+ communities',
      'Partner with 5+ Islamic schools for Keys curriculum integration',
      'Run regional workshops in Atlanta, Chicago, and Houston',
      'Build educator network for curriculum co-development',
    ],
    messagingPillars: [
      {
        title: 'Fikr as Practice',
        body: 'Reflection is not passive contemplation — it is active engagement with creation. The IRL Lens Pilot brings the platform\'s framework into physical community spaces.',
      },
      {
        title: 'Community as Curriculum',
        body: 'The best curriculum is the community itself. The Pilot phase builds learning environments where caretakers teach each other through shared observation and practice.',
      },
      {
        title: 'Physical Kits as Dawah',
        body: 'Printed materials, QR codes, and physical tools extend the platform\'s reach into spaces where screens are absent. The kit is the dawah.',
      },
    ],
    targetSegments: [
      { label: 'Islamic School Teachers', icon: '🏫' },
      { label: 'Masjid Youth Directors', icon: '🕌' },
      { label: 'Community Circle Leaders', icon: '⭕' },
      { label: 'Homeschool Networks', icon: '🏠' },
      { label: 'Environmental Justice Groups', icon: '🌍' },
      { label: 'Prison Chaplains', icon: '🤲' },
    ],
    features: ['Community circles', 'Physical caretaker kits', 'Regional workshops', 'Educator partnerships', 'Curriculum co-development'],
    ctaLabel: 'Explore the Platform →',
    ctaHref: '/guided-intro',
  },
];

// ─── CURSOR GLOW ──────────────────────────────────────────────────────────────

function useCursorGlow(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      setPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
    };
    el.addEventListener('mousemove', handler);
    return () => el.removeEventListener('mousemove', handler);
  }, [containerRef]);
  return pos;
}

// ─── SIGNUP FORM ──────────────────────────────────────────────────────────────

interface SignupState {
  name: string;
  email: string;
  role: string;
  city: string;
  referral_code: string;
}

const ROLES = [
  'Engineer', 'Teacher', 'Scientist', 'Gardener',
  'Community Organizer', 'Formerly Incarcerated', 'Student / Law Student',
  'Custodian / Janitor', 'Other',
];

function PhaseSignupForm({ phase }: { phase: RolloutPhase }) {
  const [form, setForm] = useState<SignupState>({ name: '', email: '', role: '', city: '', referral_code: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    setError('');
    try {
      const { error: supaErr } = await supabase.from('community_signups').insert([{
        name: form.name || null,
        email: form.email,
        role: form.role || null,
        city: form.city || null,
        source: `phase:${phase.slug}`,
      }]);
      if (supaErr) {
        if (supaErr.code === '23505') {
          setError('This email is already registered. JazakAllah khair!');
        } else {
          setError(supaErr.message);
        }
      } else {
        setSubmitted(true);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)' }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#c9a84c', marginBottom: 8 }}>الحمد لله</p>
        <h3 className="font-mono uppercase tracking-widest mb-4" style={{ color: '#d4c5a9', fontSize: 14 }}>Alhamdulilah. You&apos;re in.</h3>
        <p className="mb-6" style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7 }}>
          You&apos;re registered as an early adopter for <strong style={{ color: '#c9a84c' }}>{phase.name}</strong>. We&apos;ll be in touch as this phase launches.
        </p>
        <div className="flex flex-col gap-3">
          <Link href="/community" className="font-mono text-xs tracking-widest py-3 px-6 text-center" style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
            Back to Community →
          </Link>
          <Link href={phase.ctaHref} className="font-mono text-xs tracking-widest py-3 px-6 text-center" style={{ border: '1px solid rgba(45,138,135,0.4)', color: '#2d8a87' }}>
            {phase.ctaLabel}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-xl p-8 flex flex-col gap-4" style={{ background: 'rgba(6,13,18,0.7)', border: `1px solid ${phase.accentColor}30` }}>
      <input
        type="text"
        placeholder="Your name"
        value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        className="w-full px-4 py-3 font-mono text-sm outline-none"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }}
      />
      <input
        type="email"
        placeholder="your@email.com"
        value={form.email}
        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
        required
        className="w-full px-4 py-3 font-mono text-sm outline-none"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }}
      />
      <select
        value={form.role}
        onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
        className="w-full px-4 py-3 font-mono text-sm outline-none"
        style={{ background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(201,168,76,0.15)', color: form.role ? '#d4c5a9' : 'rgba(212,197,169,0.4)', borderRadius: 4 }}
      >
        <option value="">Your role (optional)</option>
        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      <input
        type="text"
        placeholder="Your city"
        value={form.city}
        onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
        className="w-full px-4 py-3 font-mono text-sm outline-none"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.15)', color: '#d4c5a9', borderRadius: 4 }}
      />
      <input
        type="text"
        placeholder="Referral code (if you have one)"
        value={form.referral_code}
        onChange={e => setForm(f => ({ ...f, referral_code: e.target.value }))}
        className="w-full px-4 py-3 font-mono text-sm outline-none"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.1)', color: '#d4c5a9', borderRadius: 4 }}
      />
      {error && <p className="font-mono text-xs" style={{ color: '#e07070' }}>{error}</p>}
      <button
        type="submit"
        disabled={loading}
        className="font-mono uppercase tracking-widest py-4 transition-all duration-300"
        style={{ background: phase.accentColor, color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em', border: 'none', cursor: loading ? 'wait' : 'pointer', borderRadius: 4 }}
      >
        {loading ? 'Joining...' : `Join ${phase.name} Early Adopters →`}
      </button>
    </form>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function PhasePage() {
  const params = useParams();
  const slug = params?.slug as string;
  const phase = ROLLOUT_PHASES.find(p => p.slug === slug);
  const heroRef = useRef<HTMLDivElement>(null);
  const pos = useCursorGlow(heroRef);

  if (!phase) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#0a1a12' }}>
        <Header />
        <div className="text-center px-6">
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(201,168,76,0.4)' }}>404 · PHASE NOT FOUND</p>
          <h1 style={{ fontFamily: "'Amiri', serif", fontSize: 40, color: '#c9a84c' }}>Phase Not Found</h1>
          <p className="mt-4 mb-8" style={{ color: '#d4c5a9' }}>This phase does not exist or has not been announced yet.</p>
          <Link href="/community" className="font-mono text-xs tracking-widest uppercase px-6 py-3" style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
            Back to Community →
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const otherPhases = ROLLOUT_PHASES.filter(p => p.slug !== slug);

  return (
    <div className="min-h-screen" style={{ background: phase.bgColor }}>
      <Header />
      <Breadcrumb
        items={[
          { label: 'Open Letter', href: '/open-letter' },
          { label: 'Community', href: '/community' },
          { label: `Phase ${phase.num}: ${phase.name}` },
        ]}
        showReturnToLetter
      />

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20"
        style={{ background: phase.bgColor }}
      >
        <div
          className="pointer-events-none absolute inset-0 transition-all duration-700"
          style={{ background: `radial-gradient(700px circle at ${pos.x}% ${pos.y}%, ${phase.accentColor}20 0%, transparent 70%)` }}
        />
        <div className="pointer-events-none absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")', backgroundSize: '200px 200px' }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Phase breadcrumb */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <Link href="/community" className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(212,197,169,0.3)' }}>
              Community
            </Link>
            <span style={{ color: 'rgba(212,197,169,0.2)' }}>›</span>
            <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(212,197,169,0.3)' }}>Phases</span>
            <span style={{ color: 'rgba(212,197,169,0.2)' }}>›</span>
            <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: phase.accentColor }}>Phase {phase.num}</span>
          </div>

          {/* Status badge */}
          <div className="flex justify-center mb-6">
            <span className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5" style={{ border: `1px solid ${phase.statusColor}50`, color: phase.statusColor, background: `${phase.statusColor}10` }}>
              {phase.status}
            </span>
          </div>

          {/* Arabic */}
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(40px, 8vw, 80px)', color: phase.accentColor, direction: 'rtl', lineHeight: 1, marginBottom: 8 }}>
            {phase.arabic}
          </p>
          <p className="font-mono text-[10px] tracking-widest uppercase mb-4" style={{ color: `${phase.accentColor}80` }}>
            {phase.arabicMeaning} · {phase.subtitle}
          </p>

          <h1
            className="mb-4"
            style={{
              fontFamily: "'Amiri', 'Georgia', serif",
              fontSize: 'clamp(28px, 5vw, 52px)',
              color: '#d4c5a9',
              lineHeight: 1.2,
            }}
          >
            {phase.name}
          </h1>

          <p className="mb-8 italic" style={{ color: 'rgba(212,197,169,0.6)', fontSize: 16 }}>
            {phase.heroTagline}
          </p>

          {/* Quranic Ayat */}
          <div className="max-w-2xl mx-auto mb-10 p-6 rounded-lg" style={{ background: `${phase.accentColor}08`, border: `1px solid ${phase.accentColor}20` }}>
            <p className="italic mb-3" style={{ color: '#d4c5a9', fontSize: 15, lineHeight: 1.7 }}>
              {phase.quranicAyat}
            </p>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {phase.quranicHref ? (
                  <a
                    href={phase.quranicHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[9px] tracking-widest uppercase flex items-center gap-1 transition-all duration-200"
                    style={{ color: `${phase.accentColor}90`, borderBottom: `1px solid ${phase.accentColor}40` }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = phase.accentColor; (e.currentTarget as HTMLElement).style.borderBottomColor = phase.accentColor; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = `${phase.accentColor}90`; (e.currentTarget as HTMLElement).style.borderBottomColor = `${phase.accentColor}40`; }}
                  >
                    {phase.quranicRef}
                    <span style={{ fontSize: '0.7em', opacity: 0.6 }}>↗</span>
                  </a>
                ) : (
                  <p className="font-mono text-[9px] tracking-widest uppercase" style={{ color: `${phase.accentColor}80` }}>
                    {phase.quranicRef}
                  </p>
                )}
                <span style={{ color: `${phase.accentColor}40`, fontSize: 10 }}>·</span>
                <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: `${phase.accentColor}60` }}>{phase.quranicTheme}</span>
              </div>
            </div>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${phase.accentColor}60`, direction: 'rtl', marginTop: 6 }}>
              {phase.quranicThemeArabic}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#signup"
              className="font-mono uppercase tracking-widest px-8 py-4 transition-all duration-300"
              style={{ background: phase.accentColor, color: '#0a1a12', fontSize: 12, letterSpacing: '0.15em', border: 'none', cursor: 'pointer' }}
            >
              Join as Early Adopter →
            </a>
            <Link
              href={phase.ctaHref}
              className="font-mono uppercase tracking-widest px-8 py-4 text-center transition-all duration-300"
              style={{ border: `1px solid ${phase.accentColor}40`, color: phase.accentColor, fontSize: 12, letterSpacing: '0.15em' }}
            >
              {phase.ctaLabel}
            </Link>
          </div>
        </div>
      </section>

      {/* ── ABOUT THIS PHASE ── */}
      <section className="py-20 px-6" style={{ background: '#f5f0e8' }}>
        <div className="max-w-3xl mx-auto">
          <p className="font-mono text-[10px] tracking-widest uppercase mb-4" style={{ color: phase.accentColor }}>
            ABOUT THIS PHASE · عن هذه المرحلة
          </p>
          <p style={{ color: '#1C2B25', fontSize: 17, lineHeight: 1.8 }}>
            {phase.description}
          </p>
        </div>
      </section>

      {/* ── GOALS ── */}
      <section className="py-20 px-6" style={{ background: '#1C2B25' }}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: `${phase.accentColor}80` }}>
              PHASE GOALS · أهداف المرحلة
            </p>
            <h2 className="font-mono uppercase tracking-widest" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              What We&apos;re Working Toward
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {phase.goals.map((goal, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-lg" style={{ background: 'rgba(6,13,18,0.5)', border: `1px solid ${phase.accentColor}15` }}>
                <span className="font-mono text-xs mt-0.5 flex-shrink-0" style={{ color: phase.accentColor }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p style={{ color: '#d4c5a9', fontSize: 13, lineHeight: 1.6 }}>{goal}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MESSAGING PILLARS ── */}
      <section className="py-20 px-6" style={{ background: '#f5f0e8' }}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: '#8a9a92' }}>
              MESSAGING PILLARS · ركائز الرسالة
            </p>
            <h2 className="font-mono uppercase tracking-widest" style={{ color: '#1C2B25', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              The Core Message
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {phase.messagingPillars.map((pillar, i) => (
              <div key={i} className="p-6 rounded-lg" style={{ background: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderTop: `3px solid ${phase.accentColor}` }}>
                <h3 className="font-mono uppercase tracking-wide mb-3" style={{ color: '#1C2B25', fontSize: 11 }}>{pillar.title}</h3>
                <p style={{ color: '#4a5a52', fontSize: 12, lineHeight: 1.7 }}>{pillar.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TARGET SEGMENTS ── */}
      <section className="py-20 px-6" style={{ background: '#0d1f16' }}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: `${phase.accentColor}60` }}>
              TARGET COMMUNITY · المجتمع المستهدف
            </p>
            <h2 className="font-mono uppercase tracking-widest" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              Who This Phase Is For
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {phase.targetSegments.map((seg, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-lg" style={{ background: 'rgba(6,13,18,0.6)', border: `1px solid ${phase.accentColor}20` }}>
                <span style={{ fontSize: 20 }}>{seg.icon}</span>
                <span className="font-mono text-[10px] tracking-wide uppercase" style={{ color: '#d4c5a9' }}>{seg.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-16 px-6" style={{ background: '#f5f0e8' }}>
        <div className="max-w-3xl mx-auto">
          <p className="font-mono text-[10px] tracking-widest uppercase mb-6" style={{ color: '#8a9a92' }}>
            PHASE FEATURES · ميزات المرحلة
          </p>
          <div className="flex flex-wrap gap-3">
            {phase.features.map((f, i) => (
              <span key={i} className="font-mono text-[10px] tracking-widest uppercase px-3 py-2 rounded" style={{ background: `${phase.accentColor}12`, border: `1px solid ${phase.accentColor}30`, color: phase.accentColor }}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── RECOGNITION ARC ── */}
      <section className="py-20 px-6" style={{ background: '#060d12' }}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-10 text-center">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: `${phase.accentColor}60` }}>
              RECOGNITION ARC · قوس المعرفة
            </p>
            <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              The Methodology of the Lens
            </h2>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, fontStyle: 'italic' }}>
              Every encounter with creation follows this arc — from first contact to crossing over.
            </p>
          </div>

          {/* Arc Stage Indicator */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
            {[
              { id: 'mata', label: 'MATA', ar: 'مَاطَا', desc: 'The Lens as Tool', color: '#c9a84c' },
              { id: 'encounter', label: 'ENCOUNTER', ar: 'مشاهدة', desc: 'Sensory Contact', color: '#2d8a87' },
              { id: 'examination', label: 'EXAMINATION', ar: 'فحص', desc: 'Three Questions', color: '#2d7a47' },
              { id: 'recognition', label: 'RECOGNITION', ar: 'معرفة', desc: 'Names Visible', color: '#c9a84c' },
              { id: 'uboor', label: 'UBOOR', ar: 'عُبُور', desc: 'Crossing Over', color: '#8a8ab8' },
            ].map((stage, i, arr) => (
              <React.Fragment key={stage.id}>
                <div className="flex flex-col items-center gap-1 px-3 py-3 rounded-lg" style={{ background: 'rgba(6,13,18,0.6)', border: `1px solid ${stage.color}25`, minWidth: 90, textAlign: 'center' }}>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: stage.color, direction: 'rtl' }}>{stage.ar}</span>
                  <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: stage.color }}>{stage.label}</span>
                  <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)' }}>{stage.desc}</span>
                </div>
                {i < arr.length - 1 && (
                  <span style={{ color: 'rgba(201,168,76,0.25)', fontSize: 14 }}>›</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Three-Question Protocol */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {[
              { q: 'Is it?', ar: 'هَلْ هُوَ؟', layer: 'ENCOUNTER', desc: 'Confirm the presence of the thing. Does it exist? Is it real? This is the first act of observation — acknowledging what is before you.' },
              { q: 'What is it?', ar: 'مَا هُوَ؟', layer: 'EXAMINATION', desc: 'Identify the nature of the thing. What Names compose it? What processes does it embody? This is the act of recognition.' },
              { q: 'When is it?', ar: 'مَتَى هُوَ؟', layer: 'SUNNAT', desc: 'Locate it in time and sequence. What stage of the Sunnat Allah is this? What came before? What comes after? This is the act of situating.' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-lg" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
                <div className="font-mono text-[8px] tracking-widest uppercase mb-2" style={{ color: 'rgba(201,168,76,0.4)' }}>{item.layer}</div>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>{item.ar}</p>
                <p className="font-mono text-[11px] tracking-wide mb-3" style={{ color: '#d4c5a9' }}>{item.q}</p>
                <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEAF SEQUENCE TUTORIAL ── */}
      <section className="py-20 px-6" style={{ background: '#0a1a12' }}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(45,122,71,0.6)' }}>
              LEAF SEQUENCE · تسلسل الورقة
            </p>
            <h2 className="font-mono uppercase tracking-widest mb-3" style={{ color: '#2d7a47', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              Sunnat Tutorial Walkthrough
            </h2>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, maxWidth: 600 }}>
              The Leaf Sequence is the primary tutorial for the IRL Lens — six Names of Allah visible in a single leaf, from divine power to provision. This is how the Lens is used.
            </p>
          </div>

          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-6 top-8 bottom-8 w-px hidden md:block" style={{ background: 'linear-gradient(to bottom, rgba(201,168,76,0.3), rgba(45,122,71,0.3), rgba(45,138,135,0.3))' }} />

            <div className="flex flex-col gap-4">
              {[
                { step: 1, name: 'Al-Qadir', ar: 'الْقَادِر', meaning: 'The All-Powerful', color: '#c9a84c', note: 'Recognition begins with acknowledging divine capacity — the power behind every observable process. Before the leaf, there is Al-Qadir.' },
                { step: 2, name: 'Al-Khaliq', ar: 'الْخَالِق', meaning: 'The Creator', color: '#c9a84c', note: 'From power comes creation — the origination of form from nothing. The leaf did not design itself. Al-Khaliq originates.' },
                { step: 3, name: 'Al-Bari', ar: 'الْبَارِئ', meaning: 'The Evolver', color: '#2d7a47', note: 'The fashioning of distinct form — each leaf shaped precisely, no two identical. Al-Bari separates and distinguishes form from formlessness.' },
                { step: 4, name: 'Al-Musawwir', ar: 'الْمُصَوِّر', meaning: 'The Fashioner of Forms', color: '#2d7a47', note: 'The imaging — the visible form given to the created thing. The color, the veins, the texture. Zahir made manifest through Al-Musawwir.' },
                { step: 5, name: 'Ar-Razzaq', ar: 'الرَّزَّاق', meaning: 'The Ever Provider', color: '#2d8a87', note: 'The leaf is a provision engine — photosynthesis as Ar-Razzaq\'s mechanism. Rizq flows through the chlorophyll. The leaf feeds the world.' },
                { step: 6, name: 'An-Nur', ar: 'النُّور', meaning: 'The Light', color: '#c9a84c', note: 'The sequence closes with light — the leaf receives An-Nur, converting divine light into sustenance. The arc completes.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-5 pl-0 md:pl-14 relative">
                  {/* Step dot */}
                  <div className="absolute left-0 top-3 w-12 h-12 rounded-full hidden md:flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}12`, border: `1px solid ${item.color}40` }}>
                    <span className="font-mono text-[10px]" style={{ color: item.color }}>{String(item.step).padStart(2, '0')}</span>
                  </div>
                  <div className="flex-1 p-5 rounded-lg" style={{ background: 'rgba(6,13,18,0.5)', border: `1px solid ${item.color}15` }}>
                    <div className="flex items-center gap-3 mb-2">
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: item.color, direction: 'rtl' }}>{item.ar}</span>
                      <div>
                        <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: item.color }}>{item.name}</span>
                        <span style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', marginLeft: 8 }}>{item.meaning}</span>
                      </div>
                    </div>
                    <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.7 }}>{item.note}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing state */}
            <div className="mt-6 p-5 rounded-lg text-center" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#c9a84c', marginBottom: 4 }}>الْحَمْدُ لِلَّه</p>
              <p className="font-mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.5)' }}>Alhamdulillah · The Closing State of Every Recognition Arc</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── UBOOR CLOSING MEDITATION ── */}
      <section className="py-20 px-6" style={{ background: '#0d0d1a' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-mono text-[10px] tracking-widest uppercase mb-4" style={{ color: 'rgba(138,138,184,0.5)' }}>
            UBOOR · عُبُور · CROSSING OVER
          </p>
          <h2 className="font-mono uppercase tracking-widest mb-6" style={{ color: '#8a8ab8', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
            The Closing Meditation
          </h2>

          <div className="p-8 rounded-xl mb-8" style={{ background: 'rgba(90,90,138,0.06)', border: '1px solid rgba(90,90,138,0.15)' }}>
            <p style={{ color: '#d4c5a9', fontSize: 16, lineHeight: 1.9, fontStyle: 'italic', marginBottom: 16 }}>
              Uboor is the crossing — from recognition to action, from seeing to doing, from the Names to the Amanah. Every recognition arc ends here. The lens has done its work. Now the caretaker acts.
            </p>
            <div className="flex flex-col gap-4 mt-6">
              <div className="p-4 rounded-lg" style={{ background: 'rgba(90,90,138,0.08)', border: '1px solid rgba(90,90,138,0.12)' }}>
                <p className="font-mono text-[9px] tracking-widest uppercase mb-2" style={{ color: 'rgba(138,138,184,0.6)' }}>✦ BREATHE</p>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7 }}>
                  Before crossing, pause. Breathe. The recognition you have just completed is a gift. The Names you have seen are real. The Amanah you are about to accept is weighty. Breathe first.
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.12)' }}>
                <p className="font-mono text-[9px] tracking-widest uppercase mb-2" style={{ color: 'rgba(45,122,71,0.6)' }}>UNIVERSAL SUBMISSION</p>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7 }}>
                  Every particle submits. The leaf submits to Al-Qadir. The river submits to Ar-Razzaq. The ecosystem submits to Al-Mizan. The IRL Lens reveals this submission — it is already happening, everywhere, in everything. You are invited to join what creation has never stopped doing.
                </p>
              </div>
            </div>
          </div>

          <p style={{ fontFamily: "'Amiri', serif", fontSize: 32, color: '#c9a84c', marginBottom: 6 }}>الْحَمْدُ لِلَّه</p>
          <p className="font-mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.5)' }}>
            Alhamdulillah · The arc is complete
          </p>
        </div>
      </section>

      {/* ── EARLY ADOPTER SIGNUP ── */}
      <section id="signup" className="py-20 px-6" style={{ background: '#0a1a12' }}>
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: `${phase.accentColor}60` }}>
              EARLY ADOPTER SIGNUP · التسجيل المبكر
            </p>
            <h2 className="font-mono uppercase tracking-widest mb-4" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              Join Phase {phase.num} Early
            </h2>
            <p style={{ color: '#d4c5a9', fontSize: 15, fontStyle: 'italic' }}>
              Be among the first to access {phase.name} when it launches.
            </p>
          </div>
          <PhaseSignupForm phase={phase} />
        </div>
      </section>

      {/* ── OTHER PHASES ── */}
      <section className="py-20 px-6" style={{ background: '#0d1f16' }}>
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 text-center">
            <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(201,168,76,0.4)' }}>
              OTHER PHASES · مراحل أخرى
            </p>
            <h2 className="font-mono uppercase tracking-widest" style={{ color: '#c9a84c', fontSize: 'clamp(13px, 2.5vw, 18px)', letterSpacing: '0.2em' }}>
              The Full Rollout
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {otherPhases.map(p => (
              <Link
                key={p.slug}
                href={`/phase/${p.slug}`}
                className="block p-5 rounded-lg transition-all duration-200 hover:opacity-80"
                style={{ background: 'rgba(6,13,18,0.6)', border: `1px solid ${p.accentColor}25`, borderTop: `3px solid ${p.accentColor}` }}
              >
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: p.accentColor, direction: 'rtl', marginBottom: 4 }}>{p.arabic}</p>
                <p className="font-mono text-[8px] tracking-widest uppercase mb-2" style={{ color: `${p.accentColor}60` }}>PHASE {p.num}</p>
                <h3 className="font-mono uppercase tracking-wide mb-2" style={{ color: '#d4c5a9', fontSize: 10 }}>{p.name}</h3>
                <span className="font-mono text-[8px] tracking-widest uppercase px-2 py-0.5" style={{ border: `1px solid ${p.statusColor}40`, color: p.statusColor }}>
                  {p.status}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes goldShimmer {
          0%, 100% { opacity: 1; text-shadow: 0 0 20px rgba(201,168,76,0.3); }
          50% { opacity: 0.85; text-shadow: 0 0 40px rgba(201,168,76,0.6); }
        }
      `}</style>
    </div>
  );
}
